export const A_VALUE = 2_861_091;

export interface PensionFormValues {
  currentAge: number;
  expectedRetirementAge: number;
  averageMonthlyIncome: number;
  contributionYears: number;
}

export interface PensionResult {
  estimatedMonthlyPension: number;
  annualPension: number;
  totalLifetimePension: number;
  replacementRate: number;
  pensionStartAge: number;
  contributionYears: number;
  isEligible: boolean;
}

export function calculatePension(values: PensionFormValues): PensionResult {
  const { averageMonthlyIncome, contributionYears, expectedRetirementAge } = values;

  const B = Math.min(Math.max(averageMonthlyIncome, 350_000), 6_170_000);
  const n = Math.min(contributionYears, 40);
  const isEligible = n >= 10;

  if (!isEligible) {
    return {
      estimatedMonthlyPension: 0,
      annualPension: 0,
      totalLifetimePension: 0,
      replacementRate: 0,
      pensionStartAge: expectedRetirementAge,
      contributionYears,
      isEligible: false,
    };
  }

  const incomeFactor = 0.5 + 0.005 * (n - 10);
  const estimatedMonthlyPension = Math.round((A_VALUE * 0.5 + B * 0.5) * incomeFactor);
  const annualPension = estimatedMonthlyPension * 12;
  const receiveYears = Math.max(0, 85 - expectedRetirementAge);
  const totalLifetimePension = estimatedMonthlyPension * 12 * receiveYears;
  const replacementRate = B > 0 ? (estimatedMonthlyPension / B) * 100 : 0;

  return {
    estimatedMonthlyPension,
    annualPension,
    totalLifetimePension,
    replacementRate,
    pensionStartAge: expectedRetirementAge,
    contributionYears,
    isEligible: true,
  };
}

export function getPensionCallout(result: PensionResult) {
  if (!result.isEligible) {
    return {
      title: '납입 기간 10년 미만은 연금 수령 불가',
      body: '국민연금은 최소 10년(120개월) 납입해야 연금으로 받을 수 있습니다. 납부 기간이 부족하면 60세 이후 반환일시금으로만 돌려받습니다. 임의가입·추납 제도를 통해 기간을 늘릴 수 있습니다.',
      cta: { label: '목표 설계 계산기로 대안 설계', href: '/retirement/goal' },
    };
  }

  if (result.replacementRate < 40) {
    return {
      title: `소득 대체율 ${result.replacementRate.toFixed(0)}% — 국민연금만으로는 부족합니다`,
      body: '국민연금은 노후 소득의 보조 역할입니다. 부족분을 IRP·ISA 등 사적 연금으로 채우는 전략이 필요합니다.',
      cta: { label: 'IRP·ISA 절세 효과 계산하기', href: '/tax-benefit' },
    };
  }

  return {
    title: `소득 대체율 ${result.replacementRate.toFixed(0)}% — 준수한 수준입니다`,
    body: '국민연금 수령 시기를 최대 5년 늦추면(연기연금) 연 7.2%씩 연금액이 늘어납니다. 건강하고 다른 자산이 있다면 연기를 고려해보세요.',
    cta: { label: '노후 자금 계산기로 전체 시나리오 확인', href: '/retirement' },
  };
}
